import clsx from "clsx";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { toastLocalizedOperationCatcher } from "../../../utils/alerts";
import { useAv } from "../../../utils/av";
import {
  DEFAULT_BREAKOUT_ADD_MORE_TIME_DURATION,
  MAX_BREAKOUT_DURATION,
  MIN_BREAKOUT_DURATION,
} from "../../../utils/breakouts";
import { getCurrentServerTimeMs, useTimeRemaining } from "../../../utils/hooks/use-time-remaining";
import { Link } from "../../../utils/routing/components/Link";
import { pageUrl } from "../../../utils/urls";
import { PROFILE_PROPERTIES, getUserFullName } from "../../../utils/user-utils";

import {
  serverEndBreakouts,
  serverExtendBreakoutTime,
  serverUpdateClassUserBreakoutGroup,
} from "api/api-server-actions";
import { NumberFieldWithStepperButtons } from "components/forms/NumberFieldWithStepperButtons";
import { BreakoutListeningToggleButton } from "components/materials/sidebar/BreakoutListeningToggleButton";
import { CloseOverlayButton } from "components/materials/sidebar/CloseOverlayButton";
import { ContentSidebarHeader } from "components/materials/sidebar/ContentSidebarHeader";
import { ContentSidebarMain } from "components/materials/sidebar/ContentSidebarMain";
import { OverlayContainer } from "components/materials/sidebar/OverlayContainer";
import { SidebarOptionsMenu } from "components/menus/SidebarOptionsMenu";
import { ClassUserType } from "components/server-types";
import { Trans, t } from "i18n/i18n";
import { AccordionPanel } from "mds/components/AccordionPanel";
import { DragAndDrop } from "mds/components/DragAndDrop";
import { IconText } from "mds/components/IconText";
import { ListItem } from "mds/components/ListItem";
import { MenuItem, SubMenu } from "mds/components/Menu";
import { Tooltip } from "mds/components/Tooltip";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import { LinkButton } from "mds/components/buttons/LinkButton";
import { SquareButton } from "mds/components/buttons/SquareButton";
import {
  AddPlusIcon,
  CircleInfoIcon,
  ClockIcon,
  CopySendArrowIcon,
  HandRaiseFilledIcon,
  PersonGroupIcon,
  PersonSingleIcon,
  SoundLevelAnimatedIcon,
  StarIcon,
} from "mds/icons";
import { useCapabilities, useUserIdsWhoCanAuthorSection } from "store/capability-hooks";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  pageSelectors,
  selectActiveClassSession,
  selectActiveClassSessionId,
  selectBreakoutGroupIdByClassUserId,
  selectClassUsersInMainRoom,
  selectCurrentClassUser,
  selectCurrentClassUserId,
  selectCurrentSectionId,
  selectUserByClassUserId,
  userSelectors,
} from "store/selectors";
import {
  selectBreakoutRoomHasRaisedHands,
  selectCurrentBreakoutGroupId,
  selectFullBreakoutGroups,
  selectIsInBreakout,
  selectOrderedBreakoutGroups,
} from "store/selectors/breakouts";
import {
  selectIsObservingVirtualClass,
  selectIsVirtualUserSpeaking,
} from "store/selectors/pubsub-virtual-class-state";
import { sortByFirstName } from "store/selectors/sorting";
import { PubsubVirtualClassStateActions } from "store/slices/pubsub-virtual-class-state";
import { useListSelector, useObjectSelector } from "store/store-hooks";

interface PersonCountProps {
  count: number;
}
const PersonCount: FC<PersonCountProps> = ({ count }) => {
  return (
    <div className="flex items-center gap-1 text-sm font-medium text-black-tint-20">
      {count}
      <PersonSingleIcon size="xs" />
    </div>
  );
};

interface BreakoutRoomControlsProps {
  count: number;
  breakoutGroupId: string;
  isObserving?: boolean;
}
const BreakoutRoomControls: FC<BreakoutRoomControlsProps> = ({
  count,
  breakoutGroupId,
  isObserving = false,
}) => {
  const dispatch = useAppDispatch();
  const currentClassUser = useAppSelector(selectCurrentClassUser);
  const currentBreakoutGroupId = useAppSelector(selectCurrentBreakoutGroupId);
  // We need to know if the user is in any breakout room to show/hide the Listen button
  const isInABreakoutRoom = !!currentBreakoutGroupId;
  // We need to know if the user is in this specific breakout room to show the Join/Leave button state
  const isInThisBreakoutRoom = currentBreakoutGroupId === breakoutGroupId;
  // We need to know if any user in this breakout room has their hand raised
  const breakoutRoomHasRaisedHands = useAppSelector((state) =>
    selectBreakoutRoomHasRaisedHands(state, breakoutGroupId),
  );

  const onJoinBreakoutGroup = () => {
    if (isObserving) {
      dispatch(PubsubVirtualClassStateActions.setObserverBreakoutGroupId(breakoutGroupId));
      return;
    }
    if (!currentClassUser) return;
    serverUpdateClassUserBreakoutGroup(currentClassUser.id, breakoutGroupId).catch(
      toastLocalizedOperationCatcher("failed_to_join_breakout_group"),
    );
  };

  const onLeaveBreakoutGroup = () => {
    if (isObserving) {
      dispatch(PubsubVirtualClassStateActions.setObserverBreakoutGroupId(null));
      return;
    }
    if (!currentClassUser) return;
    serverUpdateClassUserBreakoutGroup(currentClassUser.id, null).catch(
      toastLocalizedOperationCatcher("failed_to_move_to_main_room"),
    );
  };

  const showListenButton = !isInABreakoutRoom;

  return (
    <div className="flex items-center gap-2">
      <PersonCount count={count} />
      {showListenButton && <BreakoutListeningToggleButton breakoutGroupId={breakoutGroupId} />}
      <SquareButton
        eventName={`Breakouts - Settings / ${isInThisBreakoutRoom ? "Leave room" : "Join room"}`}
        kind={breakoutRoomHasRaisedHands && !isInThisBreakoutRoom ? "accent" : "tertiary"}
        size="xs"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (isInThisBreakoutRoom) {
            onLeaveBreakoutGroup();
          } else {
            onJoinBreakoutGroup();
          }
        }}
      >
        {breakoutRoomHasRaisedHands && !isInThisBreakoutRoom && <HandRaiseFilledIcon />}
        {isInThisBreakoutRoom
          ? t("breakouts_control_panel.leave")
          : t("breakouts_control_panel.join")}
      </SquareButton>
    </div>
  );
};

interface MoveToRoomSubMenuProps {
  classUserId: string;
}
const MoveToRoomSubMenu: FC<MoveToRoomSubMenuProps> = ({ classUserId }) => {
  const currentBreakoutGroupId = useAppSelector((state) =>
    selectBreakoutGroupIdByClassUserId(state, classUserId),
  );
  const allBreakoutGroups = useListSelector(selectOrderedBreakoutGroups);
  const breakoutGroups = useMemo(
    () => allBreakoutGroups.filter((breakoutGroup) => breakoutGroup.id !== currentBreakoutGroupId),
    [allBreakoutGroups, currentBreakoutGroupId],
  );

  const onMoveRoom = (breakoutGroupId: string) => () => {
    serverUpdateClassUserBreakoutGroup(classUserId, breakoutGroupId).catch(
      toastLocalizedOperationCatcher("failed_to_move_to_room"),
    );
  };

  return (
    <SubMenu
      label={
        <IconText
          iconStart={<CopySendArrowIcon />}
          text={t("breakouts_control_panel.move_to_room")}
        />
      }
      overflow="auto"
    >
      {breakoutGroups.map((breakoutGroup) => {
        return (
          <MenuItem
            eventName="Breakouts - Settings / Move user to another room"
            key={breakoutGroup.id}
            onClick={onMoveRoom(breakoutGroup.id)}
          >
            <IconText
              iconStart={<PersonGroupIcon />}
              text={t("breakouts_control_panel.room_number", { number: breakoutGroup.number })}
            />
          </MenuItem>
        );
      })}
    </SubMenu>
  );
};

interface BreakoutsControlMenuProps {
  classUserId: string;
}
export const BreakoutsControlMenu: FC<BreakoutsControlMenuProps> = ({ classUserId }) => {
  const breakoutGroupId = useAppSelector((state) =>
    selectBreakoutGroupIdByClassUserId(state, classUserId),
  );
  const onMoveToMainRoom = () => {
    serverUpdateClassUserBreakoutGroup(classUserId, null).catch(
      toastLocalizedOperationCatcher("failed_to_move_to_main_room"),
    );
  };

  return (
    <SidebarOptionsMenu>
      <MoveToRoomSubMenu classUserId={classUserId} />

      <MenuItem
        disabled={!breakoutGroupId}
        eventName="Breakouts - Settings / Move user to main room"
        onClick={onMoveToMainRoom}
      >
        <IconText
          iconStart={<CopySendArrowIcon />}
          text={t("breakouts_control_panel.move_to_main_room")}
        />
      </MenuItem>
    </SidebarOptionsMenu>
  );
};

interface BreakoutGroupMemberProps {
  classUserId: string;
  classUserIndex: number;
  onDropClassUser: (droppedItem: DraggableClassUserType) => void;
  onMoveClassUserLocally: (
    item: DraggableClassUserType,
    toIndex: number,
    dropContainerId: string | number | undefined,
  ) => void;
  isStarUser: boolean;
  isObserving?: boolean;
}
const BreakoutGroupMember = ({
  classUserId,
  classUserIndex,
  onDropClassUser,
  onMoveClassUserLocally,
  isStarUser,
  isObserving = false,
}: BreakoutGroupMemberProps) => {
  const breakoutGroupId = useAppSelector((state) =>
    selectBreakoutGroupIdByClassUserId(state, classUserId),
  );
  const user = useObjectSelector(
    (state) => selectUserByClassUserId(state, classUserId),
    PROFILE_PROPERTIES,
  );
  const isSpeaking = useAppSelector((state) => selectIsVirtualUserSpeaking(state, classUserId));
  if (!user) return null;

  const memberContent = (
    <ListItem
      className="flex items-center justify-between"
      grabbable={!isObserving}
      key={classUserId}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        {isStarUser ? <StarIcon size="xs" /> : <PersonSingleIcon size="xs" />}
        {getUserFullName(user)}
        {isSpeaking && <SoundLevelAnimatedIcon className="text-blue" size="m" />}
      </div>
      {!isObserving && <BreakoutsControlMenu classUserId={classUserId} />}
    </ListItem>
  );

  if (isObserving) {
    return memberContent;
  }

  return (
    <DragAndDrop
      dragItemKind="class_user"
      dropContainerId={breakoutGroupId ?? undefined}
      dropIndex={classUserIndex}
      item={
        {
          id: classUserId,
          breakout_group_id: breakoutGroupId,
          order: classUserIndex,
        } as DraggableClassUserType
      }
      key={classUserId}
      draggable
      droppable
      onDraggedItemHover={onMoveClassUserLocally}
      onDropItem={onDropClassUser}
    >
      {memberContent}
    </DragAndDrop>
  );
};

type DraggableClassUserType = Pick<ClassUserType, "id" | "breakout_group_id"> & {
  order: number;
};

const TimeLeftCard = () => {
  const classSession = useAppSelector(selectActiveClassSession);
  const endTime = classSession?.breakout_session_ends_at;
  const endDate = endTime ? new Date(endTime) : null;
  const { timeRemainingStrPadded } = useTimeRemaining(endDate);

  return (
    <div className="rounded-lg border border-blue-tint-70 bg-blue-tint-95 p-2">
      <div className="flex items-center justify-between text-sm font-medium">
        <div className="ml-1 flex items-center gap-2">
          <ClockIcon />
          {t("breakouts_control_panel.time_left")}
        </div>
        <div
          className={clsx(
            "text-left font-semibold text-blue",
            timeRemainingStrPadded.length > 5 ? "min-w-[62px]" : "min-w-[42px]",
          )}
        >
          {timeRemainingStrPadded}
        </div>
      </div>
    </div>
  );
};

interface AddMoreTimeConfigFormValues {
  breakout_duration: number;
}

const AddMoreTimeConfig = () => {
  const classSessionId = useAppSelector(selectActiveClassSessionId);

  const form = useForm<AddMoreTimeConfigFormValues>({
    defaultValues: {
      breakout_duration: DEFAULT_BREAKOUT_ADD_MORE_TIME_DURATION,
    },
  });

  const breakoutDuration = form.watch("breakout_duration");

  const hasError = !!form.formState.errors.breakout_duration;

  const onSubmit = (values: AddMoreTimeConfigFormValues) => {
    if (!classSessionId) return;
    serverExtendBreakoutTime(classSessionId, values.breakout_duration).catch(
      toastLocalizedOperationCatcher("failed_to_extend_breakout_time"),
    );
  };

  return (
    <FormProvider {...form}>
      <form className="flex flex-col" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-black-tint-20">
            {t("breakouts_control_panel.add_more_time")}
          </div>
          <div className="flex items-center gap-2">
            <NumberFieldWithStepperButtons
              label={t("breakout_group_modal.min")}
              max={MAX_BREAKOUT_DURATION}
              min={MIN_BREAKOUT_DURATION}
              {...form.register("breakout_duration", {
                required: t("error.field_required"),
                max: {
                  value: MAX_BREAKOUT_DURATION,
                  message: t("error.duration_out_of_range", {
                    min: MIN_BREAKOUT_DURATION,
                    max: MAX_BREAKOUT_DURATION,
                  }),
                },
                min: {
                  value: MIN_BREAKOUT_DURATION,
                  message: t("error.duration_out_of_range", {
                    min: MIN_BREAKOUT_DURATION,
                    max: MAX_BREAKOUT_DURATION,
                  }),
                },
                validate: {
                  isWholeNumber: (value) => {
                    const number = Number(value);
                    if (!Number.isInteger(number)) {
                      return t("error.whole_number_required");
                    }
                    return true;
                  },
                },
              })}
            />

            <Tooltip content={form.formState.errors.breakout_duration?.message}>
              <SquareButton
                disabled={hasError}
                eventName="Breakouts - Settings / Add time"
                eventProps={{ addedMinutes: breakoutDuration }}
                kind="accent"
                size="s"
                type="submit"
              >
                {t("edit_delete_widget.add")}
              </SquareButton>
            </Tooltip>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

const MANUALLY_END_BREAKOUTS_DELAY_MS = 5000;
const IMMEDIATELY_END_BREAKOUTS_THRESHOLD_MS = 9000;

const EndBreakoutsButton = () => {
  const classSession = useAppSelector(selectActiveClassSession);
  const endTime = classSession?.breakout_session_ends_at;
  const endDate = endTime ? new Date(endTime) : null;
  const { timeRemainingMs, timeRemainingStr } = useTimeRemaining(endDate);
  const [isWaitingForBreakoutsToEnd, setIsWaitingForBreakoutsToEnd] = useState(false);

  // If there are more than 9s remaining, set breakout_session_ends_at to 5 seconds from now,
  // then let useBreakoutEndObserver handle the rest. Otherwise, immediately end the breakout session.
  const onManuallyEndBreakouts = async () => {
    if (!classSession) return;

    const currentServerTimeMs = getCurrentServerTimeMs();
    if (!currentServerTimeMs) return;

    const shouldDelayEnd =
      timeRemainingMs !== null && timeRemainingMs > IMMEDIATELY_END_BREAKOUTS_THRESHOLD_MS;

    if (shouldDelayEnd) {
      try {
        await storeApi.class_sessions.partial_update({
          id: classSession.id,
          breakout_session_ends_at: new Date(
            currentServerTimeMs + MANUALLY_END_BREAKOUTS_DELAY_MS,
          ).toISOString(),
        });
        setIsWaitingForBreakoutsToEnd(true);
      } catch (error) {
        toastLocalizedOperationCatcher("failed_to_end_breakouts")(error);
      }
    } else {
      serverEndBreakouts(classSession.id);
    }
  };

  const buttonText = isWaitingForBreakoutsToEnd
    ? t("breakouts_control_panel.ending_breakouts_in_seconds", { seconds: timeRemainingStr })
    : t("breakouts_control_panel.end_breakouts_for_all");

  return (
    <SquareButton
      disabled={isWaitingForBreakoutsToEnd}
      eventName="Breakouts - Settings / End"
      kind="tertiary"
      size="s"
      onClick={onManuallyEndBreakouts}
    >
      {buttonText}
    </SquareButton>
  );
};

export const BreakoutsControlPanel: FC = () => {
  const dispatch = useAppDispatch();
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const { canAuthorSection } = useCapabilities();
  const classSession = useAppSelector(selectActiveClassSession);
  const isInBreakout = useAppSelector(selectIsInBreakout);
  const currentBreakoutGroupId = useAppSelector(selectCurrentBreakoutGroupId);
  const isInBreakoutRoom = !!currentBreakoutGroupId;
  const currentClassUserId = useAppSelector(selectCurrentClassUserId);
  const breakoutGroups = useListSelector(selectFullBreakoutGroups);
  const isObserving = useAppSelector(selectIsObservingVirtualClass);

  const starUserIds = useUserIdsWhoCanAuthorSection();
  const starUserIdSet = useMemo(() => new Set(starUserIds), [starUserIds]);
  const classUsersInMainRoom = useListSelector(selectClassUsersInMainRoom);
  const userEntities = useAppSelector(userSelectors.selectEntities);

  const sortClassUsersByFirstName = useCallback(
    (a: ClassUserType, b: ClassUserType) => {
      const userA = userEntities[a.user_id];
      const userB = userEntities[b.user_id];
      if (!userA && !userB) return 0;
      if (!userA) return 1;
      if (!userB) return -1;
      return sortByFirstName(userA, userB);
    },
    [userEntities],
  );

  const syncedPage = useAppSelector((s) =>
    pageSelectors.selectById(s, classSession?.breakout_session_synced_page_id),
  );

  const { isVirtualClassActive, isInVirtualClass } = useAv();

  const onAddBreakoutRoom = () => {
    if (!currentSectionId || isObserving) return;
    storeApi.breakout_groups
      .create({ section_id: currentSectionId })
      .catch(toastLocalizedOperationCatcher("failed_to_add_breakout_room"));
  };

  useEffect(() => {
    if (!currentSectionId) return;
    storeApi.breakout_groups.list({ section_id: currentSectionId });
    if (!classSession?.breakout_session_synced_page_id) return;
    storeApi.page_groups.list({ page_id: classSession.breakout_session_synced_page_id });
  }, [currentSectionId, classSession?.breakout_session_synced_page_id]);

  // onMoveClassUserLocally handles drag events in both BreakoutsControlPanel and BreakoutGroupMember.
  // All it does is update the breakout_group_id of the dragged ClassUser item and set it equal to the
  // dropContainerId of whatever drop zone it's hovering over. This way when onDropClassUser is called,
  // the ClassUser will have the new value for breakout_group_id already set. The value of dropContainerId
  // will be a BreakoutGroup id when hovering over a BreakoutGroup element, or null when hovering over
  // the "main call" element.
  const onMoveClassUserLocally = (
    item: DraggableClassUserType,
    _toIndex: number,
    dropContainerId: string | number | undefined,
  ) => {
    if (isObserving) return;
    // eslint-disable-next-line no-param-reassign
    item.breakout_group_id = typeof dropContainerId === "string" ? dropContainerId : null;
  };

  // onDropClassUser handles drop events in both BreakoutsControlPanel and BreakoutGroupMember. It assumes
  // the current value of breakout_group_id set on the dragged item was set there by onMoveClassUserLocally,
  // and it performs the appropriate API request.
  const onDropClassUser = (droppedItem: DraggableClassUserType) => {
    if (isObserving) return;
    serverUpdateClassUserBreakoutGroup(droppedItem.id, droppedItem.breakout_group_id).catch(
      toastLocalizedOperationCatcher("failed_to_move_to_room"),
    );
  };

  const onJoinMainRoom = () => {
    if (isObserving) {
      dispatch(PubsubVirtualClassStateActions.setObserverBreakoutGroupId(null));
      return;
    }
    if (!currentClassUserId) return;
    serverUpdateClassUserBreakoutGroup(currentClassUserId, null).catch(
      toastLocalizedOperationCatcher("failed_to_move_to_main_room"),
    );
  };

  if (
    !isInBreakout ||
    !canAuthorSection ||
    !isVirtualClassActive ||
    (!isInVirtualClass && !isObserving)
  ) {
    return null;
  }

  return (
    <OverlayContainer className="overflow-hidden bg-white">
      <ContentSidebarHeader panelType="overlay" visibleOnMobile>
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-hibiscus-tint-95">
            <PersonGroupIcon className="text-hibiscus" size="xs" />
          </div>
          <h3>{t("breakouts_control_panel.title")}</h3>
        </div>

        <CloseOverlayButton />
      </ContentSidebarHeader>

      <ContentSidebarMain bottomPadding={false} className="min-h-0">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            {syncedPage ? (
              <div className="flex items-center gap-1 truncate text-xs font-medium">
                <TooltipTrigger
                  content={t("group_configuration_card.breakouts_synced_with_this_page_tooltip")}
                >
                  <CircleInfoIcon size="s" />
                </TooltipTrigger>
                <Trans
                  i18nKey="breakouts_control_panel.breakouts_synced_with_page"
                  t={t}
                  values={{ title: syncedPage.title }}
                >
                  <Link
                    className="truncate text-blue underline"
                    to={pageUrl(syncedPage.section_id, syncedPage.id)}
                  >
                    {syncedPage.title}
                  </Link>
                </Trans>
              </div>
            ) : null}

            <DragAndDrop
              dragItemKind="class_user"
              dropContainerId={undefined}
              droppable={!isObserving}
              onDraggedItemHover={onMoveClassUserLocally}
              onDropItem={onDropClassUser}
            >
              <AccordionPanel
                childrenWrapperClassName="mt-2.5"
                emptyContent={
                  <div className="mb-1 ml-1 text-sm">{t("breakouts_control_panel.empty_room")}</div>
                }
                expandedColor="red"
                title={t("breakouts_control_panel.main_room")}
                titleClassName="text-red-shade-20"
                titleRightContent={
                  <div className="flex items-center gap-2">
                    <PersonCount count={classUsersInMainRoom.length} />
                    {isInBreakoutRoom && (
                      <SquareButton
                        eventName={`Breakouts - Settings / "Join main room"}`}
                        kind="tertiary"
                        size="xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          onJoinMainRoom();
                        }}
                      >
                        {t("breakouts_control_panel.join")}
                      </SquareButton>
                    )}
                  </div>
                }
                expandableAnywhere
                startOpen
              >
                {classUsersInMainRoom
                  .toSorted(sortClassUsersByFirstName)
                  .map((classUser, index) => (
                    <BreakoutGroupMember
                      classUserId={classUser.id}
                      classUserIndex={index}
                      isObserving={isObserving}
                      isStarUser={starUserIdSet.has(classUser.user_id)}
                      key={classUser.id}
                      onDropClassUser={onDropClassUser}
                      onMoveClassUserLocally={onMoveClassUserLocally}
                    />
                  ))}
              </AccordionPanel>
            </DragAndDrop>

            {breakoutGroups.map((group) => (
              <DragAndDrop
                dragItemKind="class_user"
                dropContainerId={group.id}
                droppable={!isObserving}
                key={group.id}
                onDraggedItemHover={onMoveClassUserLocally}
                onDropItem={onDropClassUser}
              >
                <AccordionPanel
                  childrenWrapperClassName="mt-2.5"
                  emptyContent={
                    <div className="mb-1 ml-1 text-sm">
                      {t("breakouts_control_panel.empty_room")}
                    </div>
                  }
                  expandedColor="blue"
                  title={t("breakouts_control_panel.room_number", { number: group.number })}
                  titleRightContent={
                    <BreakoutRoomControls
                      breakoutGroupId={group.id}
                      count={group.members.length}
                      isObserving={isObserving}
                    />
                  }
                  expandableAnywhere
                  startOpen
                >
                  {group.members
                    .toSorted(sortClassUsersByFirstName)
                    .map((member, classUserIndex) => (
                      <BreakoutGroupMember
                        classUserId={member.id}
                        classUserIndex={classUserIndex}
                        isObserving={isObserving}
                        isStarUser={starUserIdSet.has(member.user_id)}
                        key={member.id}
                        onDropClassUser={onDropClassUser}
                        onMoveClassUserLocally={onMoveClassUserLocally}
                      />
                    ))}
                </AccordionPanel>
              </DragAndDrop>
            ))}
            {/* Gate functionality that requires classUser */}
            {!isObserving && (
              <LinkButton
                eventName="Breakouts - Settings / Add new room"
                size="s"
                onClick={onAddBreakoutRoom}
              >
                <IconText
                  iconStart={<AddPlusIcon />}
                  text={t("breakouts_control_panel.add_new_room")}
                />
              </LinkButton>
            )}
          </div>

          <TimeLeftCard />

          {!isObserving && (
            <>
              <AddMoreTimeConfig />
              <EndBreakoutsButton />
            </>
          )}
        </div>
      </ContentSidebarMain>
    </OverlayContainer>
  );
};
